import { Alert, Button, Group, Modal, Stack } from "@mantine/core";
import { useCallback, type FC } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { IconAlertTriangle } from "@tabler/icons-react";
import { useTRPC } from "../../../../trpc";
import { captureEvent } from "../../../../utils/posthog";
import { Event } from "../../../../constants/events";
import type { Tileset } from "../types/tileset";
import { useMutation, useQueryClient } from "@tanstack/react-query";

interface DeleteTilesetModalProps {
  open: boolean;
  onClose: () => void;
  tileset: Tileset;
}

export const DeleteTilesetModal: FC<DeleteTilesetModalProps> = ({ onClose, open, tileset }) => {
  const {
    id: tilesetId,
    key: tilesetKey,
  } = tileset;

  const intl = useIntl();
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const { mutateAsync: deleteTileset, isPending } = useMutation(trpc.deleteTileset.mutationOptions());

  const handleDelete = useCallback(() => {
    captureEvent(Event.TilesetDeleteTileset);

    void deleteTileset(tilesetId).then(() => {
      void Promise.all([
        queryClient.invalidateQueries(trpc.getTilesets.queryFilter()),
        queryClient.invalidateQueries(trpc.getTilesetsWithVersions.queryFilter()),
      ]).then(() => {
        onClose();
      });
    }).catch((error: unknown) => {
      logger.error({ error }, "Failed to delete tileset:");
    });
  }, [deleteTileset, tilesetId, onClose, queryClient, trpc]);

  return (
    <Modal
      opened={open}
      onClose={onClose}
      title={intl.formatMessage({ defaultMessage: "Delete tileset", id: "title.deleteTileset" })}
    >
      <Stack gap="xs">
        <FormattedMessage
          id="text.deleteTilesetDescription"
          defaultMessage={"Are you sure you want to delete the tileset \"{tileset}\"? This action cannot be undone."}
          values={{ tileset: tilesetKey }}
        />

        <Alert variant="light" color="red" icon={<IconAlertTriangle />}>
          <FormattedMessage
            defaultMessage="Deleting this tileset will remove it from your account and any maps using it may no longer display correctly."
            id="text.deleteTilesetWarning"
          />
        </Alert>

        <Group gap="xs" justify="flex-end" pt="lg">
          <Button variant="secondary" type="button" loading={isPending} onClick={onClose}>
            <FormattedMessage defaultMessage="Cancel" id="button.cancel" />
          </Button>

          <Button variant="danger" type="button" loading={isPending} onClick={handleDelete}>
            <FormattedMessage defaultMessage="Delete" id="button.delete" />
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
